import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import classNames from 'classnames';
import { isMobile } from 'react-device-detect';
import * as ReactDOM from 'react-dom';
import {
  getFilterData,
  getPaginationOpts, getResponseError,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import Pagination from '../../../components/base/Pagination/Pagination';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { showGlobalConfirm, showSignPopup, signDocument } from '../../System/actions';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import {
  dateTimeFormatter,
  documentStatusFormatter, localeFormatter, subjectFormatter,
  tooltipFormatter,
  userPositionFormatter,
} from '../../../utils/formatters';
import {
  deleteDocument, groupDeleteFile, groupSignFile, loadAwaitDocument, loadAwaitDocumentClear,
  loadAwaitDocumentIdList, loadDocumentById,
} from '../actions';
import { getAwaitDocumentData } from '../reducer';
import DropdownList from '../../../components/base/Dropdown/DropdownList';
import ViewDocument from '../../System/components/ViewDocument';
import {
  REGISTRY_STATUS, TYPE_ROLLBACK_ENUM,
} from '../../../configs';
import CheckBoxInput from '../../../components/fields/CheckBoxInput/CheckBoxInput';
import { APPLICATION_MODULE } from '../../../configs/moduleNames';
import getDefaultSort from '../../../utils/getDefaultSort';
import { VersionListDialog } from '../../RollbackVersion/components/VersionListDialog';
import { RollbackPlanDialog } from '../../RollbackVersion/components/RollbackPlanDialog';
import { createRollbackPlans } from '../../RollbackVersion/actions';
import { getRollbackVersionData } from '../../RollbackVersion/reducer';
import RowFilterForm from '../../FilterTemplate/components/RowFilterForm';
import { PAGE_TYPE } from '../../FilterTemplate/components/filter-settings';
import TimerLoader from '../../../components/base/Loader/TimerLoader';
import MobileApplicationHistoryFooter from './MobileApplicationHistoryFooter';
import PropertyGrid from '../../../components/base/Grid/PropertyGrid';
import FilterModal from '../../FilterTemplate/components/FilterModal';

export default function AwaitDocumentPage() {
  const { formatMessage } = useIntl();
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [showView, setShowView] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const store = useStore();
  const { updatePeriod } = useSelector(getSystemData);
  const [isBatchCommand, setIsBatchCommand] = useState(false);
  const [selected, setSelected] = useState([]);
  const [isAllPageRowsSelected, setIsAllPageRowsSelected] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const document_id = searchParams.get('document_id');
  const [showHistoryVersion, setShowHistoryVersion] = useState(false);
  const [showRollbackPlan, setShowRollbackPlan] = useState(false);
  const [isOpenFilter, setOpenFilter] = useState(false);
  const {
    awaitDocumentMeta,
    loadingAwaitDocument,
    awaitDocument,
    currentDocument,
    loadingDeleteDocument,
    loadingCurrentApplication,
    loadingReturnApplication,
    loadingDeniedApplication,
    loadingSuspendedApplication,
  } = useSelector(getAwaitDocumentData);

  const {
    rollbackPlan,
  } = useSelector(getRollbackVersionData);

  const defaultFilterValues = {
    status: REGISTRY_STATUS.INACTIVE,
    is_actual: true,
  };

  const {
    control, getValues, setValue, watch, reset,
  } = useForm({ defaultValues: getFilterData('document-filter', defaultFilterValues) });

  const loadData = opts => {
    if (opts && opts.page) {
      setSelected([]);
    }
    const filter = getValues();
    const paginationOpts = getPaginationOpts(store.getState()[APPLICATION_MODULE].awaitDocumentMeta, opts); // костыль внедрен из за потери контекста в updatePeriod. Он сохранял первую функцию
    const requestData = {
      ...paginationOpts,
      filter,
      sort,
    };
    const p1 = dispatch(loadAwaitDocument(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const loadById = id => {
    if (!id && !document_id) return;
    dispatch(loadDocumentById(id || document_id))
      .then(() => setShowView(true))
      .catch(response => errorNotification('Загрузка документа', getResponseError(response)));
  };

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  useEffect(() => {
    const subscription = watch(values => loadData({ page: 1 }));
    return () => subscription.unsubscribe();
  }, [watch]);
  useEffect(() => () => {
    dispatch(loadAwaitDocumentClear());
  }, []);
  useEffect(loadById, [document_id]);

  const handleSelectRow = item => {
    let select = item;
    if (selectedRow && selectedRow.id === item.id) {
      select = null;
    }
    setSelectedRow(select);
  };

  const handleSign = item => event => {
    if (!item) {
      return;
    }
    dispatch(showSignPopup()).then(cert => {
      dispatch(signDocument({ cert, document: item }))
        .then(() => {
          successNotification('Подписание документа', 'Подписание прошло успешно');
          loadData();
        })
        .catch(error => {
          errorNotification('Подписание документа', error || 'Ошибка подписания файла');
        });
    }).catch(() => {});
  };

  const handleDelete = item => event => {
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление документа №${item.name}`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteDocument(item.id)).then(() => {
        if (selectedRow === item.id) {
          setSelectedRow(null);
        }
        loadData();
        successNotification('Удаление документа', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление документа', getResponseError(response));
      });
    }).catch(() => {});
    return false;
  };

  const handleSetUrlParams = params => {
    const result = Object.keys(params).reduce((acc, c) => {
      if (params[c]) {
        acc[c] = params[c];
      }
      return acc;
    }, {});
    setSearchParams(result);
  };

  const handleShow = item => event => {
    if (!item) {
      return;
    }
    handleSetUrlParams({ document_id: item.id });
  };

  const handleClose = () => {
    setShowView(false);
    setSearchParams({});
    setShowHistoryVersion(false);
    setShowRollbackPlan(false);
    setIsBatchCommand(false);
  };

  const handlePackageSign = (item, isBach = false) => event => {
    if (!isBach && !item) {
      return;
    }
    if (!isBach && item) {
      setSelectedRow(item);
    } else {
      setIsBatchCommand(isBach);
    }
    sign(!isAllPageRowsSelected ? selected : []);
  };

  const sign = (selected = []) => {
    dispatch(showSignPopup())
      .then(cert => {
        dispatch(groupSignFile({ filter: getValues(), cert, selected }))
          .then(() => {
            setSelected([]);
            loadData();
            setIsAllPageRowsSelected(false);
            successNotification('Пакетное подписание документов', 'Подписание прошло успешно');
          })
          .catch(responseLogWithNotification);
      }).catch(() => {});
  };

  const handlePackageDelete = () => {
    dispatch(groupDeleteFile({ filter: getValues() }))
      .then(() => {
        loadData();
        successNotification('Пакетное удаление документов', 'Удаление прошло успешно');
      })
      .catch(responseLogWithNotification);
  };

  const handleCreateRollbackPlan = item => {
    if (!item) {
      return;
    }
    let root_models;
    if (Array.isArray(item)) {
      root_models = item?.map(id => ({
        root_model_type: 'document',
        root_model_id: id,
      }));
    } else if (item?.version) {
      root_models = [{
        target_version: item?.version,
        root_model_type: 'document',
        root_model_id: item?.subject_id || item?.id,
      }];
    } else {
      root_models = [{
        root_model_type: 'document',
        root_model_id: item?.subject_id || item?.id,
      }];
    }

    dispatch(createRollbackPlans({ root_models })).then(() => setShowRollbackPlan(true)).catch(responseLogWithNotification);
    setShowHistoryVersion(false);
  };

  const handleRollbackPlanGroup = () => () => {
    if (!Array.isArray(selected) || !selected?.length) {
      return;
    }
    if (isAllPageRowsSelected) {
      dispatch(loadAwaitDocumentIdList({
        filter: getValues(),
      })).then(data => {
        setSelected(data?.data);
        handleCreateRollbackPlan(data?.data);
      }).catch(responseLogWithNotification);
    } else {
      handleCreateRollbackPlan(selected);
    }
  };

  useEffect(() => {
    if (isAllPageRowsSelected && isBatchCommand) {
      dispatch(loadAwaitDocumentIdList({
        filter: getValues(),
      })).then(data => {
        setSelected(data?.data);
      }).catch(responseLogWithNotification);
    }
  }, [isAllPageRowsSelected, isBatchCommand]);

  const checkBoxColumnFormatter = (_, item) => {
    const check = selected.includes(item.id);
    return (
      <div className="bordered-checkbox" onClick={e => { setSelected(check ? selected.filter(e => e !== item.id) : [...selected, item.id]); e.stopPropagation(); }}>
        <CheckBoxInput
          inputProps={{
            value: check,
          }}
        />
      </div>
    );
  };

  const checkBoxHeaderFormatter = () => {
    const isAllRowsSelected = selected?.length === awaitDocument?.length && selected?.length && awaitDocument?.length && !isAllPageRowsSelected;
    return (
      <div
        className="bordered-checkbox"
        onClick={() => {
          if (selected?.length >= awaitDocument?.length) {
            if (isAllPageRowsSelected) {
              setSelected([]);
            }
            setIsAllPageRowsSelected(!isAllPageRowsSelected);
            return;
          }
          setSelected(selected.length < awaitDocument?.length ? awaitDocument.map(e => e.id) : []);
        }}
      >
        <CheckBoxInput
          className={classNames({ 'some-child': selected.length && !isAllPageRowsSelected && !isAllRowsSelected }, { 'all-child': isAllPageRowsSelected })}
          inputProps={{
            value: isAllRowsSelected,
          }}
        />
      </div>
    );
  };

  const handleShowHistoryVersion = item => () => {
    if (!item) {
      return;
    }
    setSelectedRow(item);
    setShowHistoryVersion(true);
  };

  const columns = [
    !isMobile
      ? {
        dataField: 'checkBox',
        headerClassName: 'check-box text-center column-relative',
        dataClassName: 'align-middle column-relative text-center justify-content-center',
        formatter: checkBoxColumnFormatter,
        headerFormatter: checkBoxHeaderFormatter,
      } : null,
    {
      dataField: 'created_at',
      text: 'Дата и время',
      headerClassName: 'date-with-time',
      formatter: dateTimeFormatter,
    },
    {
      dataField: 'number',
      text: 'Номер',
      headerClassName: 'module-column',
    },
    {
      dataField: 'notification_template',
      text: 'Наименование итогового документа',
      headerClassName: 'module-column',
      formatter: a => (a && a.name ? tooltipFormatter(a.name) : ''),
    },
    {
      dataField: 'context',
      text: 'Субъект',
      headerClassName: 'module-column',
      formatter: (_, item) => subjectFormatter(item),
    },
    {
      dataField: 'creator',
      text: 'Автор',
      headerClassName: 'user-column',
      formatter: userPositionFormatter,
    },
    {
      dataField: 'status',
      text: 'Статус',
      headerClassName: 'status-column',
      formatter: documentStatusFormatter,
    },
    {
      dataField: 'signed_at',
      text: 'Дата подписания',
      headerClassName: 'status-column',
      formatter: dateTimeFormatter,
    },
    {
      dataField: 'signer',
      text: 'Подписант',
      headerClassName: 'status-column',
      formatter: a => (a ? userPositionFormatter(a) : null),
    },
    {
      dataField: 'is_actual',
      text: 'Актуальность',
      dataClassName: 'align-middle',
      formatter: e => localeFormatter('document.actual', Number(e)),
    },
  ].filter(column => column);

  const [sort, setSort] = useState(getDefaultSort(columns));

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  useEffect(() => { loadData(); }, [sort]);

  const renderContextMenu = item => (
    <div className="more-wrapper">
      <Button variant="link" className="w-100 more-button" onClick={handleShow(item)}>
        Просмотреть
      </Button>
      {item?.is_actual && !isMobile
        ? (
          <Button variant="link" className="w-100 more-button" onClick={handleSign(item)}>
            Подписать
          </Button>
        ) : null }
      {/* <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}> */}
      {/*  Удалить */}
      {/* </Button> */}
      {!isMobile ? (
        <Button variant="link" className="w-100 more-button" onClick={handleShowHistoryVersion(item)}>
          История изменений
        </Button>
      ) : null}
      {!isMobile ? (
        <Button variant="link" className="w-100 more-button" onClick={() => handleCreateRollbackPlan(item)}>
          Восстановить предыдущий статус
        </Button>
      ) : null}
    </div>
  );

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={renderContextMenu(item)}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const renderFooter = () => {
    const element = document.getElementById('app-content-footer');
    if (!element) {
      return null;
    }
    return ReactDOM.createPortal(<MobileApplicationHistoryFooter
      control={control}
      defaultFilterValues={defaultFilterValues}
      getValues={getValues}
      reset={reset}
      page={PAGE_TYPE.APPLICATION_DOCUMENTS}
    />, element);
  };

  return (
    <div className="page application-page">
      <TimerLoader global loading={loadingAwaitDocument || loadingDeleteDocument} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.application.documents' })}</div>
      </div>
      {!isMobile ? (
        <FilterRow>
          <RowFilterForm
            control={control}
            getValues={getValues}
            defaultValues={{}}
            hideTemplate
            page={PAGE_TYPE.APPLICATION_DOCUMENTS_SHORT}
          />
          <div className="filter-sep" />
          <Button variant="second" onClick={() => setOpenFilter(true)}>
            <Icons customIcon className="file-download" width={24} height={24} icon="filter" />
          </Button>
          {!isMobile ? (
            <>
              <DropdownList
                className="ml-3 ms-3"
                buttonClassName=""
                variant="second"
                label={(
                  <>
                    <span className="load-button-label-text">Пакетная обработка</span>
                    <Icons className="load-icon-arrow ms-2" icon="arrow-down" width={18} customIcon />
                  </>
                )}
              >
                <Button variant="link" className="button-alternate menu-button" onClick={handlePackageSign(null, true)}>
                  <Icons className="load-icon me-2" icon="pencil" customIcon />
                  Подписать документы
                </Button>
                {/* <Button variant="link" className="button-alternate menu-button delete-button" onClick={handlePackageDelete}> */}
                {/*  <Icons className="load-icon me-2" icon="trash" customIcon /> */}
                {/*  Удалить */}
                {/* </Button> */}
                <Button
                  variant="link"
                  className="button-alternate menu-button grey-text"
                  onClick={handleRollbackPlanGroup()}
                >
                  Массовый откат версий
                </Button>
              </DropdownList>
              <Button className="ms-3" variant="second" onClick={handleShow(selectedRow)} disabled={!selectedRow || !selectedRow.id}>Просмотреть</Button>
            </>
          ) : null}
          {isOpenFilter
            ? (
              <FilterModal
                control={control}
                onClose={() => setOpenFilter(false)}
                getValues={getValues}
                reset={reset}
                defaultValues={defaultFilterValues}
                page={PAGE_TYPE.APPLICATION_DOCUMENTS}
              />
            )
            : null}
        </FilterRow>
      ) : null}
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          {!isMobile ? (
            <div className="grid-wrapper frame">
              <div className="custom-scroll-theme sticky-table-wrapper">
                <CustomizedGrid
                  onClickByRow={handleSelectRow}
                  onDoubleClickByRow={(item, event) => handleShow(item)(event)}
                  rowClassFormatter={item => (selectedRow && item.id === selectedRow.id ? 'clickable-table-row selected' : 'clickable-table-row')}
                  data={awaitDocument}
                  columns={columns}
                  changeSort={changeSort}
                  sort={sort}
                  name="await-document-list"
                  settingsFormatter={moreFormatter}
                  renderContextMenu={renderContextMenu}
                />
              </div>
            </div>
          ) : (
            <PropertyGrid
              onClickByRow={handleSelectRow}
              onDoubleClickByRow={(item, event) => handleShow(item)(event)}
              rowClassFormatter={item => (selectedRow && item.id === selectedRow.id ? 'clickable-table-row selected' : 'clickable-table-row')}
              data={awaitDocument}
              columns={columns}
              changeSort={changeSort}
              sort={sort}
              name="await-document-list"
              settingsFormatter={moreFormatter}
              renderContextMenu={renderContextMenu}
            />
          )}
          <Pagination meta={awaitDocumentMeta} list={awaitDocument} handleChangeMeta={loadData} />
        </div>
      </div>
      {showView && currentDocument
        ? (
          <ViewDocument
            loadData={loadData}
            loadById={loadById}
            handleClose={handleClose}
            document={currentDocument}
            loadingCurrentApplication={loadingCurrentApplication}
            loadingReturnApplication={loadingReturnApplication}
            loadingDeniedApplication={loadingDeniedApplication}
            loadingSuspendedApplication={loadingSuspendedApplication}
            isSignAvailable
            prevCrumbs={(
              <>
                <span className="breadcrumbs-prev">Обращение</span>
                <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
                <span className="breadcrumbs-prev">Документы на согласование</span>
                <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
              </>
          )}
          />
        ) : null}
      {selectedRow?.id && showHistoryVersion ? <VersionListDialog type={TYPE_ROLLBACK_ENUM.DOCUMENT} id={selectedRow?.id} handleCreateRollbackPlan={handleCreateRollbackPlan} onClose={handleClose} /> : null}
      {rollbackPlan?.id && showRollbackPlan ? <RollbackPlanDialog rollbackPlanId={rollbackPlan?.id} onClose={handleClose} /> : null}
      {isMobile ? renderFooter() : null}
    </div>
  );
}
